import { Submission_AssignmentFeature as AssignmentFeature } from '__generated__/graphql-types';

import { HONORS_TRACK } from 'pages/open-course/common/models/tracks';

import { isGradedLabsEnabled } from 'bundles/academic-integrity/lib/epics';
import {
  COACH_ITEM_TYPENAME_OVERRIDE,
  ROLE_PLAY_PLUGIN_NAME,
  SOCRATIC_DIALOGUE_PLUGIN_NAME,
} from 'bundles/coach-item/common/constants';
import type { ContentSummary } from 'bundles/course-v2/types/ContentSummary';
import { REAL_WORLD_EXAMPLE_TYPE_NAMES } from 'bundles/learner-progress/constants';

import _t from 'i18n!nls/learner-progress';

export type ToHumanReadableTypeNameOptions = {
  trackId?: string;
  contentSummary?: ContentSummary;
  isGuidedProject?: boolean;
  isAiGradingEligible?: boolean;
  isVerifiedSkillsCourse?: boolean;
  customDisplayTypenameOverride?: string;
};

/**
 * Given a onDemandCourseMaterialItems.v2 type name, return
 * a human readable type name.
 */
export default function toHumanReadableTypeName(
  typeName: string,
  options: ToHumanReadableTypeNameOptions = {}
): string {
  const {
    trackId,
    contentSummary,
    isGuidedProject,
    isAiGradingEligible,
    isVerifiedSkillsCourse,
    customDisplayTypenameOverride,
  } = options;

  // using contentSummary[typeName] for graphql query response in degree-home/utils/ItemState.js
  // @ts-expect-error TODO: contentSummary[typeName] is casted to any for typeName: string
  const itemContentSummary = (contentSummary && (contentSummary.definition || contentSummary[typeName])) || {};
  switch (typeName) {
    case 'assessOpenSinglePage':
    case 'quiz':
      return _t('Practice Quiz');
    case 'peer':
      if (isAiGradingEligible) {
        return _t('Practice Graded Assignment');
      }
      return _t('Practice Peer-graded Assignment');
    case 'closedPeer':
    case 'gradedPeer':
    case 'phasedPeer':
      if (itemContentSummary.isMentorGraded) {
        return _t('Mentor Graded Assignment');
      } else if (trackId === HONORS_TRACK) {
        return _t('Honors Peer-graded Assignment');
      } else {
        if (isAiGradingEligible) {
          return _t('Graded Assignment');
        }
        return _t('Peer-graded Assignment');
      }
    case 'ungradedProgramming':
      return _t('Practice Programming Assignment');
    case 'gradedProgramming':
    case 'programming':
      if (trackId === HONORS_TRACK) {
        return _t('Honors Programming Assignment');
      } else {
        return _t('Programming Assignment');
      }
    case 'exam':
      if (trackId === HONORS_TRACK) {
        return _t('Honors Quiz');
      } else {
        return _t('Quiz');
      }
    case 'splitPeerReviewItem':
      return _t('Review Your Peers');
    case 'discussionPrompt':
      return _t('Discussion Prompt');
    case 'gradedDiscussionPrompt':
      return _t('Graded Discussion Prompt');
    case 'teammateReview':
      return _t('Teammate Review');
    case 'staffGraded':
      if (
        isVerifiedSkillsCourse &&
        itemContentSummary.assignmentFeatures?.includes(AssignmentFeature.VerifiedSkillsAssessment)
      ) {
        return _t('Assessment');
      } else if (
        isVerifiedSkillsCourse &&
        itemContentSummary.assignmentFeatures?.includes(AssignmentFeature.VerifiedSkillsDiagnostic)
      ) {
        // No prefix is shown for skill diagnostic items
        return '';
      } else if (itemContentSummary.isTeamSubmissionEnabled) {
        return _t('Graded Team Assignment');
      } else {
        return _t('Graded Assignment');
      }
    case 'ungradedAssignment':
      if (
        isVerifiedSkillsCourse &&
        itemContentSummary.assignmentFeatures?.includes(AssignmentFeature.VerifiedSkillsAssessment)
      ) {
        return _t('Assessment');
      } else if (
        isVerifiedSkillsCourse &&
        itemContentSummary.assignmentFeatures?.includes(AssignmentFeature.VerifiedSkillsDiagnostic)
      ) {
        // No prefix is shown for skill diagnostic items
        return '';
      } else if (itemContentSummary.isTeamSubmissionEnabled) {
        return _t('Practice Team Assignment');
      } else {
        return _t('Practice Assignment');
      }
    case 'lessonChoice':
      return _t('Lesson Choice');
    case 'assignmentGroup':
      return _t('Assignment Group');
    case 'workspaceLauncher':
      return _t('Workspace');
    case 'gradedLab':
      return _t('Graded lab');
    case 'ungradedLab':
      return isGradedLabsEnabled() ? _t('Practice lab') : _t('Lab');
    case 'supplement':
      return _t('Reading');
    case 'lecture':
      return _t('Video');
    case 'notebook':
      return _t('Notebook');
    case 'singleQuestionSubmit':
    case 'ungradedLti':
      if (isGuidedProject) {
        return _t('Guided Project');
      } else {
        return _t('Ungraded App Item');
      }
    case 'gradedLti':
      if (isGuidedProject) {
        return _t('Guided Project');
      } else {
        return _t('Graded App Item');
      }
    case 'ungradedWidget':
      if (itemContentSummary?.iframeTitle === SOCRATIC_DIALOGUE_PLUGIN_NAME) {
        return _t('Dialogue');
      }

      if (itemContentSummary?.iframeTitle === ROLE_PLAY_PLUGIN_NAME) {
        return _t('Role Play');
      }
      return _t('Ungraded Plugin');
    case 'gradedWidget':
      return _t('Graded Plugin');
    case 'coach':
      // Return custom display type name override if provided
      if (customDisplayTypenameOverride === COACH_ITEM_TYPENAME_OVERRIDE.ROLE_PLAY) {
        return _t('Role Play');
      }
      // By Default - return 'Dialogue'
      return _t('Dialogue');
    case 'wiseFlow':
      return _t('External Item from WISEflow');
    case 'placeholder':
      return _t('Deprecated Item');
    case 'injectable':
      if (REAL_WORLD_EXAMPLE_TYPE_NAMES.includes(itemContentSummary.injectableTypeName)) {
        return _t('Real-world application');
      }
      return _t('Unknown Activity');
    default:
      if (trackId === HONORS_TRACK) {
        return _t('Honors');
      } else {
        return '';
      }
  }
}
